// Phase SIKA-5e/3 — Goal switcher popover.
//
// Anchored on the composer's + button. Lists active + in-progress
// goals with a progress meta line, marks the current one, and offers
// a "Start a new goal" footer that links to /sika (where the goal
// creation flow lives).
//
// Closes on:
//   - outside click (parent owns the trigger; this component just
//     fires onClose when the scrim handler fires)
//   - Escape key
//   - any goal row click
//
// Promotion-not-restriction copy throughout (N04).

import { useEffect } from "react";
import { Link } from "react-router-dom";

import type { SikaGoalWithMessages } from "../../../sika/useSikaGoals.ts";

export interface GoalPopoverProps {
  /** Whether the popover is open. */
  open: boolean;
  /** Called when the user dismisses (Escape / outside click handler in parent). */
  onClose: () => void;
  /** Goals to display — typically the result of useSikaGoals().goals
   * filtered to non-terminal statuses by the caller. */
  goals: ReadonlyArray<SikaGoalWithMessages>;
  /** Currently selected goal id, if any. */
  activeGoalId?: string | null;
  /** Called with the picked goal id. The parent should also close. */
  onSelect: (goalId: string) => void;
}

function formatCompact(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return Math.round(n).toString();
  if (n < 100_000) {
    const k = n / 1000;
    return `${k.toFixed(1).replace(/\.0$/, "")}K`;
  }
  return `${Math.floor(n / 1000)}K`;
}

function goalMeta(g: SikaGoalWithMessages): string {
  const current = Number(g.current_amount ?? 0);
  const target = Number(g.target_amount ?? 0);
  if (target <= 0) return g.status;
  const pct = Math.round((current / target) * 100);
  return `${formatCompact(current)} / ${formatCompact(target)} · ${pct}%`;
}

export function GoalPopover({
  open,
  onClose,
  goals,
  activeGoalId = null,
  onSelect,
}: GoalPopoverProps): JSX.Element | null {
  // Escape-to-close. Bound only while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="sc2-goal-popover"
      role="menu"
      aria-label="Switch goal"
      data-testid="sc2-goal-popover"
    >
      {goals.length === 0 ? (
        <div
          style={{
            padding: 14,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.04em",
          }}
          data-testid="sc2-goal-popover-empty"
        >
          No active goals yet.
        </div>
      ) : (
        goals.map((g) => {
          const active = g.id === activeGoalId;
          return (
            <button
              key={g.id}
              type="button"
              className="sc2-goal-row"
              data-active={active}
              data-testid={`sc2-goal-row-${g.id}`}
              role="menuitemradio"
              aria-checked={active}
              onClick={() => {
                onSelect(g.id);
                onClose();
              }}
              style={{
                border: "none",
                background: "transparent",
                width: "100%",
                textAlign: "left",
              }}
            >
              <span aria-hidden style={{ width: 14, display: "inline-block" }}>
                {active ? "◉" : "○"}
              </span>
              <span style={{ flex: "1 1 auto", minWidth: 0 }}>{g.title}</span>
              <span className="sc2-goal-row-meta">{goalMeta(g)}</span>
            </button>
          );
        })
      )}
      <div
        style={{
          height: 1,
          background: "var(--sa-hairline)",
          margin: "6px 4px",
        }}
        aria-hidden
      />
      <Link
        to="/sika"
        onClick={onClose}
        className="sc2-goal-row"
        data-testid="sc2-goal-popover-new"
        style={{
          color: "var(--sa-accent-saffron)",
          textDecoration: "none",
        }}
      >
        <span aria-hidden style={{ width: 14, display: "inline-block" }}>
          +
        </span>
        <span>Start a new goal</span>
      </Link>
    </div>
  );
}
